import dayjs from 'dayjs';

import { GpuDeviceMetadata, GpuMetrics, GpuMetricsResponse } from '@cast/types';
import { toNumOrNull } from '@cast/utils';
import { fillGaps } from 'utils/metrics';
import { toGibOrNull } from 'features/cluster/workload-optimization/_utils';

import {
  NormalizedGpuData,
  NormalizedGpuDevice,
  NormalizedGpuInterval,
} from '../_components/GpuOptimizationCharts/_types';

const normalizeInterval = (item: GpuMetrics): NormalizedGpuInterval => ({
  timestamp: item.timestamp ?? '',
  gpuPercent: item.gpuUtilization != null ? item.gpuUtilization * 100 : null,
  memoryUsedGB: toGibOrNull(item.memoryUsedMib),
  deviceCount: item.deviceCount ?? null,
  recGpu: item.recGpuUtilization != null ? item.recGpuUtilization * 100 : null,
  recMemoryUsedGB: toGibOrNull(item.recMemoryUsedMib),
  recDeviceCount: item.recDeviceCount ?? null,
});

const formatDeviceLabel = (device: GpuDeviceMetadata) => {
  const mem = `${toGibOrNull(device.memoryTotalMib)} GB`;
  const count = device.count ? ` × ${device.count}` : '';
  return `${device.modelName} · ${mem}${count}`;
};

export const normalizeGpuData = (
  data: GpuMetricsResponse
): NormalizedGpuData => {
  const items = data.items ?? [];
  const rawIntervals = items.map(normalizeInterval);

  const intervals = (() => {
    if (rawIntervals.length <= 1) return rawIntervals;

    const loadedLength = dayjs
      .utc(rawIntervals[rawIntervals.length - 1].timestamp)
      .diff(dayjs.utc(rawIntervals[0].timestamp));

    const [unit, step] = (() => {
      if (loadedLength <= dayjs.duration(23, 'hours').asMilliseconds())
        return ['second', 30] as const;
      if (loadedLength <= dayjs.duration(2, 'days').asMilliseconds())
        return ['minute', 5] as const;
      if (loadedLength <= dayjs.duration(6, 'days').asMilliseconds())
        return ['minute', 10] as const;
      return ['minute', 15] as const;
    })();

    return fillGaps({
      timeSeries: rawIntervals,
      dateRange: {
        from: dayjs.utc(rawIntervals[0].timestamp),
        to: dayjs.utc(rawIntervals[rawIntervals.length - 1].timestamp),
      },
      filler: {
        gpuPercent: null,
        memoryUsedGB: null,
        deviceCount: null,
        recGpu: null,
        recMemoryUsedGB: null,
        recDeviceCount: null,
      },
      unit,
      step,
    });
  })();

  let avgGpuUtilization: number | null = null;
  let avgRecGpuUtilization: number | null = null;
  let avgMemoryUsedGB: number | null = null;
  let avgRecMemoryUsedGB: number | null = null;
  let avgDeviceCount: number | null = null;
  let avgRecDeviceCount: number | null = null;

  if (items.length > 0) {
    const gpuItems = items.filter((item) => item.gpuUtilization != null);
    if (gpuItems.length > 0) {
      avgGpuUtilization =
        (gpuItems.reduce((sum, item) => sum + (item.gpuUtilization ?? 0), 0) /
          gpuItems.length) *
        100;
    }

    const recGpuItems = items.filter((item) => item.recGpuUtilization != null);
    if (recGpuItems.length > 0) {
      avgRecGpuUtilization =
        (recGpuItems.reduce(
          (sum, item) => sum + (item.recGpuUtilization ?? 0),
          0
        ) /
          recGpuItems.length) *
        100;
    }

    const memItems = items.filter((item) => item.memoryUsedMib != null);
    if (memItems.length > 0) {
      avgMemoryUsedGB = toGibOrNull(
        memItems.reduce((sum, item) => sum + (item.memoryUsedMib ?? 0), 0) /
          memItems.length
      );
    }

    const recMemItems = items.filter((item) => item.recMemoryUsedMib != null);
    if (recMemItems.length > 0) {
      avgRecMemoryUsedGB = toGibOrNull(
        recMemItems.reduce(
          (sum, item) => sum + (item.recMemoryUsedMib ?? 0),
          0
        ) / recMemItems.length
      );
    }

    const deviceCountItems = items.filter((item) => item.deviceCount != null);
    if (deviceCountItems.length > 0) {
      avgDeviceCount = toNumOrNull(
        deviceCountItems.reduce(
          (sum, item) => sum + (item.deviceCount ?? 0),
          0
        ) / deviceCountItems.length
      );
    }

    const recDeviceCountItems = items.filter(
      (item) => item.recDeviceCount != null
    );
    if (recDeviceCountItems.length > 0) {
      avgRecDeviceCount = toNumOrNull(
        recDeviceCountItems.reduce(
          (sum, item) => sum + (item.recDeviceCount ?? 0),
          0
        ) / recDeviceCountItems.length
      );
    }
  }

  const normalizeDevices = (
    devices: GpuDeviceMetadata[]
  ): NormalizedGpuDevice[] =>
    devices.map((d) => ({
      name: d.modelName || '',
      label: formatDeviceLabel(d),
    }));

  return {
    // gpu utilization chart metrics box data
    avgGpuUtilization,
    avgRecGpuUtilization,

    // gpu memory chart metrics box data
    avgMemoryUsedGB,
    avgRecMemoryUsedGB,

    // gpu count chart metrics box data
    avgDeviceCount,
    avgRecDeviceCount,

    // charts data
    intervals,

    // devices labels
    gpuDeviceLabels: normalizeDevices(data.gpuDevices ?? []),
    recGpuDevicesLabels: normalizeDevices(data.recGpuDevices ?? []),
  };
};
